import React from 'react';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import { IconWrapper } from 'components/Tab/tab.styled';
import { SvgIcon } from 'components';
import { analytics } from 'core/analytics';
import {
  NotifCount,
  TabsWrapper,
  Tabs,
  Tab,
  LinkTabStyled,
  Dot,
} from 'apps/components/shared';

import { Summary, Cube, Task, ToolTipSettings } from 'icons';

import { clientStore } from 'apps/client/app';

@withNamespaces()
@observer
export class ProjectTabsBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      allowBanner: true,
    };
  }

  // FIXME This function is repeated in apps/payment/subscription/index.js
  subscribe = () => {
    const { paymentSpace, paymentSpacePreview, NewSubscriptionQuery } =
      this.props.subscriptionManager;
    if (!paymentSpace) return;
    if (
      !paymentSpacePreview ||
      (paymentSpacePreview && !paymentSpacePreview.preview)
    ) {
      NewSubscriptionQuery({ preview: true });
    }
    analytics.sendEvent('clicked-upgrade-banner');
    this.props.payment.ConfirmSubscribe();
  };

  hideBanner = () => {
    this.setState({ allowBanner: false });
  };

  render() {
    const { route, project, repoManager } = this.props;
    const content = project && project.content;
    const routeName = route.name;
    const routeParams = Object.assign({}, route.params); // avoid deleting the basepath from the router state
    const projectRouteSegments = route.name.split('.');
    const projectRoute = `${projectRouteSegments[0]}.${projectRouteSegments[1]}`;
    const versionRoute = `${projectRouteSegments[0]}.${
      projectRouteSegments[1]
    }${!repoManager.atHead ? '.version' : ''}`;
    const filesTabActive =
      routeName.includes('files') ||
      routeName.endsWith('file') ||
      routeName.includes('contribution') ||
      routeName.endsWith('repoDirectory') ||
      routeName.endsWith('repoFile');

    const atContributionPage = route.name.endsWith('contribution');
    delete routeParams.basepath;
    if (atContributionPage) {
      delete routeParams.contributionId;
    }

    const openIssuesCount =
      project.manager.issueTrackerAppManager?.issueTracker?.openIssuesCount;

    const conflictManager = clientStore.context.get('conflictsManager');
    const hasConflicts =
      conflictManager && conflictManager.conflicts.length > 0;
    if (project) {
      return (
        <>
          <TabsWrapper responsive={route.name.includes('project.conflicts')}>
            <Tabs responsive={route.name.includes('project.conflicts')}>
              <Tab
                data-testid='project-overview-tab'
                active={
                  routeName.endsWith('project') ||
                  routeName.endsWith('project.version')
                }
              >
                <LinkTabStyled
                  name={
                    atContributionPage
                      ? routeName.replace('.contribution', '')
                      : `${versionRoute}`
                  }
                  params={routeParams}
                  content={content}
                >
                  <div className='inline-block flex justify-center items-center h-5'>
                    <IconWrapper>
                      <SvgIcon
                        color={
                          routeName.endsWith('project') ||
                          routeName.endsWith('project.version')
                            ? 'primary'
                            : undefined
                        }
                        size='medium'
                        icon={<Summary />}
                      />
                    </IconWrapper>
                    <Trans>Overview</Trans>
                  </div>
                </LinkTabStyled>
              </Tab>
              <Tab
                active={filesTabActive}
                hasCount={true}
                data-intercom-target='project-files'
                data-testid='project-files-tab'
              >
                <LinkTabStyled
                  name={
                    atContributionPage
                      ? `${routeName.replace('.contribution', '.files')}`
                      : `${versionRoute}.files`
                  }
                  params={routeParams}
                  content={content}
                >
                  <div className='flex justify-center items-center h-5'>
                    <IconWrapper>
                      <SvgIcon
                        color={filesTabActive ? 'primary' : undefined}
                        size='medium'
                        icon={<Cube />}
                      />
                    </IconWrapper>
                    <Trans>Files</Trans>
                    {repoManager &&
                      repoManager.repository &&
                      repoManager.repository.hasOpenContribution && (
                        <Dot>(Local Changes)</Dot>
                      )}
                  </div>
                </LinkTabStyled>
              </Tab>
              {hasConflicts || route.name.includes('project.conflicts') ? (
                <Tab active={route.name.includes('project.conflicts')}>
                  <LinkTabStyled
                    name={`${projectRoute}.conflicts`}
                    params={routeParams}
                    content={content}
                  >
                    <div className='flex justify-center items-center h-5'>
                      Conflicts
                    </div>
                  </LinkTabStyled>
                </Tab>
              ) : null}
              <Tab
                active={routeName.includes('issue')}
                hasCount={project && openIssuesCount}
                data-intercom-target='project-issues'
                data-testid='project-tasks-tab'
              >
                <LinkTabStyled
                  name={`${projectRoute}.issues`}
                  params={routeParams}
                  content={content}
                >
                  <div className='flex justify-center items-center h-5 relative'>
                    <IconWrapper>
                      <SvgIcon
                        color={
                          routeName.includes('issue') ? 'primary' : undefined
                        }
                        size='medium'
                        icon={<Task />}
                      />
                    </IconWrapper>
                    <Trans>Tasks</Trans>
                    {project && openIssuesCount > 0 && (
                      <NotifCount
                        active={routeName.includes('issue')}
                        isStatic
                        variant='success'
                        isPill
                      >
                        {openIssuesCount}
                      </NotifCount>
                    )}
                  </div>
                </LinkTabStyled>
              </Tab>
              {project && project.canUpdate && (
                <Tab
                  data-testid='project-settings-tab'
                  active={routeName.includes('settings')}
                >
                  <LinkTabStyled
                    name={`${projectRoute}.settings.teams`}
                    params={routeParams}
                    content={content}
                  >
                    <div className='flex justify-center items-center h-5'>
                      <IconWrapper>
                        <SvgIcon
                          color={
                            routeName.includes('settings')
                              ? 'primary'
                              : undefined
                          }
                          size='medium'
                          icon={<ToolTipSettings />}
                        />
                      </IconWrapper>
                      <Trans>Settings</Trans>
                    </div>
                  </LinkTabStyled>
                </Tab>
              )}
            </Tabs>
          </TabsWrapper>
        </>
      );
    }
    return null;
  }
}

ProjectTabsBar.propTypes = {
  children: PropTypes.node,
  route: PropTypes.object,
  project: PropTypes.object,
  repoManager: PropTypes.object,
  payment: PropTypes.object,
  subscriptionManager: PropTypes.object,
};
